import { useRef } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { uploadFile } from '@/api/uploads.api';
import { useAuthStore } from '@/store/auth.store';
import type { UploadMimeType } from '@/types/upload.types';
import { mktDisbursePcmpProposal } from '../api/pcmpProposals.api';
import { pcmpActionCallbacks } from '../utils/pcmpActionCallbacks';

export interface PcmpMktDisburseInput {
  proof: File;
  mimeType: UploadMimeType;
  comment: string;
}

/**
 * Marketing disbursement: uploads the payment proof, then records it. The
 * upload is reused when only the second step failed and is retried.
 */
export function useMktDisbursePcmpProposal(proposalId: number, onSuccess: () => void) {
  const queryClient = useQueryClient();
  const userId = useAuthStore((state) => state.user?.id);
  const uploaded = useRef<{ file: File; key: string } | null>(null);

  return useMutation({
    mutationFn: async ({ proof, mimeType, comment }: PcmpMktDisburseInput) => {
      if (userId === undefined) throw new Error('You are not signed in.');
      if (uploaded.current?.file !== proof) {
        const key = await uploadFile({
          file: proof,
          mimeType,
          resourceType: 'PCMP_DISBURSEMENT',
          userId,
        });
        uploaded.current = { file: proof, key };
      }
      return mktDisbursePcmpProposal(proposalId, { proofKey: uploaded.current.key, comment });
    },
    ...pcmpActionCallbacks(queryClient, 'Marketing disbursement recorded', onSuccess),
  });
}
